import type {
    CreateStoryResponse,
    GetFinalizedStoryResponse,
    GetStoryResponse,
    NextStepResponse,
    QuotaStatus,
    Story,
    StoryProgressResponse,
    SubmitDrawingResponse,
    Theme,
} from "@api/types";
import type { UserPreferences, UserProfile, UserProfileUpdateRequest } from "../types/preferences";
import { config } from "./config";
import { ApiError, type ApiErrorResponse, createApiError, createNetworkError } from "./errors";
import { addAPICallBreadcrumb } from "./sentryBreadcrumbs";
import { supabase } from "./supabase";

const API_URL = config.api.url;

export class ApiClient {
    private baseURL: string;

    constructor() {
        this.baseURL = API_URL;
    }

    /**
     * Creates a structured API error with proper categorization
     */
    private createApiError(response: Response, errorData: ApiErrorResponse, operation: string): ApiError {
        return createApiError(response, errorData, operation);
    }

    /**
     * Safely extracts error data from response with fallback
     */
    private async extractErrorData(response: Response, fallbackCode: string): Promise<ApiErrorResponse> {
        try {
            return await response.json();
        } catch {
            return {
                error: {
                    code: fallbackCode,
                    message: `Request failed with status ${response.status}`,
                },
            };
        }
    }

    /**
     * Get JWT token from Supabase auth
     */
    private async getAuthToken(): Promise<string | null> {
        try {
            const {
                data: { session },
                error,
            } = await supabase.auth.getSession();

            if (error) {
                console.error("Error getting auth session:", error);
                return null;
            }

            return session?.access_token || null;
        } catch (error) {
            console.error("Error getting auth token:", error);
            return null;
        }
    }

    /**
     * Make authenticated API request
     */
    private async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
        const token = await this.getAuthToken();
        const method = options.method || "GET";
        const endpoint = url.replace(this.baseURL, "");

        const headers: HeadersInit = {
            "Content-Type": "application/json",
            ...options.headers,
            ...(token && { Authorization: `Bearer ${token}` }),
        };

        // Add breadcrumb for API call start
        addAPICallBreadcrumb(endpoint, method);

        const response = await fetch(url, {
            ...options,
            headers,
        });

        // Add breadcrumb for API call completion with status
        addAPICallBreadcrumb(endpoint, method, response.status);

        // Handle auth errors specifically
        if (response.status === 401) {
            const errorData = await this.extractErrorData(response, "AUTH_ERROR");
            throw this.createApiError(response, errorData, "Authentication");
        }

        return response;
    }

    /**
     * Health check (no auth required)
     */
    async healthCheck(): Promise<{ status: string; timestamp: string; version?: string; auth?: string }> {
        const response = await fetch(`${this.baseURL}/api/health`);
        if (!response.ok) {
            throw new Error(`Health check failed: ${response.status}`);
        }
        return response.json();
    }

    /**
     * Generate story topics (auth required)
     */
    async generateStoryTopics(genre?: string): Promise<string[]> {
        try {
            const url = `${this.baseURL}/api/stories/topics${genre ? `?genre=${encodeURIComponent(genre)}` : ""}`;
            const response = await this.authenticatedFetch(url);

            if (!response.ok) {
                const errorData = await this.extractErrorData(response, "TOPIC_GENERATION_ERROR");
                throw this.createApiError(response, errorData, "Topic generation");
            }

            const data = await response.json();
            return data.topics || ["Space Adventure", "Magical Forest", "Underwater World", "Flying Castle"];
        } catch (error) {
            // Let structured API errors (including 429 quota errors) bubble up unchanged
            if (error instanceof ApiError) {
                throw error;
            }
            // Wrap unexpected errors (JSON parsing, network failures) for user-friendly display
            throw createNetworkError("Topic generation", error as Error);
        }
    }

    /**
     * Create a new story (auth required)
     */
    async createStory(topic: string): Promise<CreateStoryResponse> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories`, {
            method: "POST",
            body: JSON.stringify({ topic }),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Story creation");
        }

        return response.json();
    }

    /**
     * Get user stories with optional limit (auth required)
     */
    async getUserStories(limit?: number): Promise<{
        stories: Story[];
        count: number;
        hasMore: boolean;
    }> {
        const url = new URL(`${this.baseURL}/api/stories`);
        if (limit) {
            url.searchParams.set("limit", limit.toString());
        }

        const response = await this.authenticatedFetch(url.toString());

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get user stories");
        }

        return response.json();
    }

    /**
     * Get paginated user stories with search and sorting (auth required)
     */
    async getUserStoriesPaginated(
        page: number = 1,
        limit: number = 10,
        sortBy: "updated_at" | "topic" = "updated_at",
        sortOrder: "asc" | "desc" = "desc",
        search?: string
    ): Promise<{
        stories: Story[];
        pagination: {
            total: number;
            page: number;
            limit: number;
            totalPages: number;
            hasNext: boolean;
            hasPrev: boolean;
        };
    }> {
        const url = new URL(`${this.baseURL}/api/stories`);
        url.searchParams.set("page", page.toString());
        url.searchParams.set("limit", limit.toString());
        url.searchParams.set("sortBy", sortBy);
        url.searchParams.set("sortOrder", sortOrder);
        if (search && search.trim()) {
            url.searchParams.set("search", search.trim());
        }

        const response = await this.authenticatedFetch(url.toString());

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get paginated stories");
        }

        return response.json();
    }

    /**
     * Get a specific story with its steps (auth required)
     */
    async getStory(storyId: string): Promise<GetStoryResponse | GetFinalizedStoryResponse> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get story");
        }

        return response.json();
    }

    /**
     * Get story for print (auth required, premium only)
     */
    async getStoryForPrint(storyId: string): Promise<GetFinalizedStoryResponse & { isPrintVersion: boolean }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/print`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get story for print");
        }

        return response.json();
    }

    /**
     * Submit drawing for a story step (auth required)
     */
    async submitStoryDrawing(
        storyId: string,
        stepNumber: number,
        imageData: string,
        imageDataCompressed: string,
        sketchingTimeMs?: number
    ): Promise<SubmitDrawingResponse & { followupText?: string }> {
        const response = await this.authenticatedFetch(
            `${this.baseURL}/api/stories/${storyId}/steps/${stepNumber}/drawings`,
            {
                method: "POST",
                body: JSON.stringify({
                    imageData,
                    imageDataCompressed,
                    ...(sketchingTimeMs !== undefined && { sketchingTimeMs }),
                }),
            }
        );

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Story drawing submission");
        }

        return response.json();
    }

    /**
     * Get AI encouragement while drawing (auth required, premium only)
     */
    async getDrawingEncouragement(
        storyId: string,
        stepNumber: number,
        imageDataCompressed: string
    ): Promise<{ encouragement: string }> {
        const response = await this.authenticatedFetch(
            `${this.baseURL}/api/stories/${storyId}/steps/${stepNumber}/encourage`,
            {
                method: "POST",
                body: JSON.stringify({
                    imageDataCompressed,
                }),
            }
        );

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Drawing encouragement");
        }

        return response.json();
    }

    /**
     * Update story progress (auth required)
     */
    async updateStoryProgress(
        storyId: string,
        currentStep: number,
        submissionId: string
    ): Promise<StoryProgressResponse & { message: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/progress`, {
            method: "PUT",
            body: JSON.stringify({ currentStep, submissionId }),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Story progress update");
        }

        return response.json();
    }

    /**
     * Progress story to next step or finalize if on final step (auth required)
     */
    async progressToNextStep(storyId: string): Promise<NextStepResponse> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/next-step`, {
            method: "POST",
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Story progression");
        }

        return response.json();
    }

    /**
     * Delete a story (soft delete, auth required)
     */
    async deleteStory(storyId: string): Promise<{
        message: string;
        story: Story;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}`, {
            method: "DELETE",
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Story deletion");
        }

        return response.json();
    }

    /**
     * Delete all user data
     */
    async deleteAllUserData(): Promise<{
        message: string;
        result: { deletedStories: number };
        timestamp: string;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/account`, {
            method: "DELETE",
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "User data deletion");
        }

        return response.json();
    }

    /**
     * Export all user data in COPPA-compliant format
     */
    async exportUserData(): Promise<{
        account_summary: {
            export_date: string;
            total_stories: number;
            account_created: string;
        };
        stories: Array<{
            title: string;
            topic: string;
            created: string;
            status: string;
            story_activities: Array<{
                step: number;
                drawing_prompt: string;
                drawing_submitted: string;
                ai_response: string;
                submission_count: number;
            }>;
        }>;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/account/export`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "User data export");
        }

        return response.json();
    }

    /**
     * Fetch authenticated thumbnail blob (always succeeds with either drawing or fallback icon)
     */
    async fetchStoryThumbnail(storyId: string): Promise<Blob> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/thumbnail`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Thumbnail fetch");
        }

        return response.blob();
    }

    /**
     * Get user quota status (auth required)
     */
    async getQuotaStatus(): Promise<QuotaStatus> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/quota`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Quota status fetch");
        }

        const data = await response.json();
        return data.quota;
    }

    /**
     * Submit feedback (auth required)
     */
    async submitFeedback(data: {
        feedbackType: string;
        message: string;
    }): Promise<{ success: boolean; message: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/feedback/submit`, {
            method: "POST",
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Feedback submission");
        }

        return response.json();
    }

    /**
     * Check if user is authenticated
     */
    async isAuthenticated(): Promise<boolean> {
        const token = await this.getAuthToken();
        return token !== null;
    }

    /**
     * Create a share link for a story (auth required)
     */
    async createStoryShare(
        storyId: string,
        expiresInDays?: number
    ): Promise<{
        share: {
            id: string;
            story_id: string;
            share_token: string;
            created_by: string;
            created_at: string;
            expires_at?: string;
            original_expires_at?: string;
        };
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/share`, {
            method: "POST",
            body: JSON.stringify(expiresInDays ? { expiresInDays } : {}),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Create story share");
        }

        return response.json();
    }

    /**
     * Revoke a story share (auth required)
     */
    async revokeStoryShare(storyId: string): Promise<{ message: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/share`, {
            method: "DELETE",
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Revoke story share");
        }

        return response.json();
    }

    /**
     * Get current share info for a story (auth required)
     */
    async getStoryShare(storyId: string): Promise<{
        hasShare: boolean;
        share?: {
            id: string;
            story_id: string;
            share_token: string;
            created_by: string;
            created_at: string;
            expires_at?: string;
            original_expires_at?: string;
        };
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/stories/${storyId}/share`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get story share");
        }

        return response.json();
    }

    /**
     * Get a shared story (no auth required)
     */
    async getSharedStory(shareToken: string): Promise<GetFinalizedStoryResponse & { isShared: true }> {
        const response = await fetch(`${this.baseURL}/api/public/story/share/${shareToken}`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get shared story");
        }

        return response.json();
    }

    /**
     * Get user preferences (auth required)
     */
    async getUserPreferences(): Promise<{
        preferences: {
            display: { showVoiceButton: boolean; theme: Theme };
            canvas: { defaultBrushSize: number; defaultBrushColor: string };
        };
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/preferences`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get user preferences");
        }

        return response.json();
    }

    /**
     * Update user preferences (auth required)
     */
    async updateUserPreferences(
        preferences: UserPreferences
    ): Promise<{ preferences: UserPreferences; message: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/preferences`, {
            method: "PUT",
            body: JSON.stringify(preferences),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Update user preferences");
        }

        return response.json();
    }

    /**
     * Get user consents (auth required)
     */
    async getUserConsents(): Promise<{
        consents: Record<string, { consented: boolean; date: string }>;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/preferences/consents`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get user consents");
        }

        return response.json();
    }

    /**
     * Update user consent (auth required)
     */
    async updateUserConsent(
        consentType: string,
        consented: boolean
    ): Promise<{
        message: string;
        consentType: string;
        consented: boolean;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/preferences/consent`, {
            method: "POST",
            body: JSON.stringify({ consentType, consented }),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Update user consent");
        }

        return response.json();
    }

    /**
     * Get user account information (auth required)
     */
    async getUserAccount(): Promise<{ tier: "free" | "plus" | "pro" }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/account/profile`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get user account");
        }

        return response.json();
    }

    /**
     * Get user tier status (auth required)
     */
    async getTierStatus(): Promise<{
        tier: "free" | "plus" | "pro";
        paymentPlan: "free" | "subscription" | "lifetime" | "trial";
        status: "active" | "canceled" | "past_due";
        expires_at?: string;
        trial_started_at?: string;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/tiers/status`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get tier status");
        }

        return response.json();
    }

    /**
     * Create checkout session for tier upgrade
     */
    async createTierCheckout(
        tier: "plus" | "pro",
        billingCycle: "monthly" | "annual" | "lifetime"
    ): Promise<{ checkout_url: string; checkout_id: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/tiers/checkout`, {
            method: "POST",
            body: JSON.stringify({ tier, billing_cycle: billingCycle }),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Create tier checkout");
        }

        return response.json();
    }

    /**
     * Get customer portal URL for billing management
     */
    async getCustomerPortalUrl(): Promise<{ portal_url: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/tiers/portal`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get customer portal");
        }

        return response.json();
    }

    /**
     * Get trial status for current user
     */
    async getTrialStatus(): Promise<{
        hasUsedTrial: boolean;
        isCurrentlyOnTrial: boolean;
        trialStartedAt?: string;
        trialExpiresAt?: string;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/tiers/trial/status`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get trial status");
        }

        return response.json();
    }

    /**
     * Start free trial for current user
     */
    async startFreeTrial(): Promise<{
        message: string;
        tier: "plus";
        payment_plan: "trial";
        expires_at: string;
        trial_started_at: string;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/tiers/trial/start`, {
            method: "POST",
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Start trial");
        }

        return response.json();
    }

    /**
     * Get user profile (auth required)
     */
    async getUserProfile(): Promise<{
        profile: UserProfile | null;
    }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/profile`);

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Get user profile");
        }

        return response.json();
    }

    /**
     * Update user profile (auth required)
     */
    async updateUserProfile(profileData: UserProfileUpdateRequest): Promise<{ profile: UserProfile; message: string }> {
        const response = await this.authenticatedFetch(`${this.baseURL}/api/profile`, {
            method: "PUT",
            body: JSON.stringify(profileData),
        });

        if (!response.ok) {
            const errorData = await this.extractErrorData(response, "UNKNOWN_ERROR");
            throw this.createApiError(response, errorData, "Update user profile");
        }

        return response.json();
    }
}

export const apiClient = new ApiClient();
